import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, CompassColor, Text } from "@noom/wax-component-library";

import { CheckboxOptions, CheckboxOption } from "@/components/survey/checkbox";

import { QuestionComponentProps } from "../../Question";

import { BaseQuestion } from "./BaseQuestion";

type CheckboxQuestionComponentProps = Omit<
  QuestionComponentProps,
  "surveyAnswers"
> & {
  options: CheckboxOption[];
  ctaText?: string;
  hiddenOptions?: CheckboxOption[];
  isCtaSticky?: boolean;
  questionHelperText?: string;
  questionId: string;
  questionText: string;
  selectedItems: string[];
};

const isSpecialCheckbox = (optionId: string) =>
  optionId === "none" || optionId === "preferNotToSay";

export const CheckboxQuestion: React.FC<CheckboxQuestionComponentProps> = ({
  canProceedWithoutAnswer = false,
  ctaText,
  isCtaSticky = false,
  onClickBack,
  onClickNext,
  options,
  hiddenOptions,
  questionHelperText,
  questionId,
  questionText,
  selectedItems,
}) => {
  const { t } = useTranslation();
  const [checkedItems, setCheckedItems] = useState(selectedItems);
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const selectedHiddenOptions =
    hiddenOptions?.filter((option) => selectedItems.includes(option.id)) ?? [];
  const notSelectedHiddenOptions =
    hiddenOptions?.filter((option) => !selectedItems.includes(option.id)) ?? [];

  const toggleCheckedState = (
    optionId: string,
    currentCheckedState: boolean,
  ) => {
    /**
     * If the item is currently checked: remove the item from the array.
     * If the item is currently not checked:
     * - if it is a special checkbox, make that the only checked item
     * - otherwise, remove existing special checkboxes and add the item to the
     *   array
     */
    let newCheckedItems: string[];
    if (currentCheckedState) {
      newCheckedItems = checkedItems.filter(
        (checkedItem) => checkedItem !== optionId,
      );
    } else if (isSpecialCheckbox(optionId)) {
      newCheckedItems = [optionId];
    } else {
      newCheckedItems = [
        ...checkedItems.filter(
          (checkedItem) => !isSpecialCheckbox(checkedItem),
        ),
        optionId,
      ];
    }
    setCheckedItems(newCheckedItems);
  };

  const submitCheckboxQuestion = () => {
    if (checkedItems.length === 0 && !canProceedWithoutAnswer) {
      return;
    }
    onClickNext(checkedItems);
  };

  const ctaButton = (
    <Button
      colorScheme="primary"
      disabled={!canProceedWithoutAnswer && checkedItems.length === 0}
      onClick={() => submitCheckboxQuestion()}
      size="xl"
      w={isCtaSticky ? "100%" : undefined}
    >
      {ctaText ?? t("common.next")}
    </Button>
  );

  return (
    <BaseQuestion
      onClickBack={onClickBack}
      questionHelperText={questionHelperText}
      questionId={questionId}
      questionText={questionText}
    >
      <CheckboxOptions
        checkedItems={checkedItems}
        options={
          showHiddenItems
            ? [
                ...options,
                ...selectedHiddenOptions,
                ...notSelectedHiddenOptions,
              ]
            : [...options, ...selectedHiddenOptions]
        }
        submitCheckboxQuestion={submitCheckboxQuestion}
        toggleCheckedState={toggleCheckedState}
      />
      {!!notSelectedHiddenOptions.length && !showHiddenItems && (
        <Button
          variant="unstyled"
          w="100%"
          onClick={() => setShowHiddenItems(true)}
        >
          <Text textDecoration="underline">
            {t("checkboxQuestion.showHiddenOptionsText")}
          </Text>
        </Button>
      )}
      {isCtaSticky ? (
        // Stack wraps each child, so `sticky` has no room to stick; on small
        // screens pin the CTA to the viewport and reserve its height so options
        // stay visible. On desktop it sits right under the options instead.
        <Box height={{ base: "var(--spacing-xxxl)", lg: "auto" }}>
          <Box
            backgroundColor={CompassColor.white}
            bottom={0}
            left="50%"
            maxWidth="400px"
            padding={{ base: "var(--spacing-m)", lg: "0" }}
            position={{ base: "fixed", lg: "static" }}
            transform={{ base: "translateX(-50%)", lg: "none" }}
            width="100%"
          >
            {ctaButton}
          </Box>
        </Box>
      ) : (
        ctaButton
      )}
    </BaseQuestion>
  );
};
